import { PropsWithChildren } from 'react';

import { FormControlLabel, Radio, RadioGroup } from '@mui/material';
import Divider from '@mui/material/Divider';
import * as yup from 'yup';

import {
  Button,
  ConditionalWrapper,
  DialogActions,
  Tooltip,
  Typography,
  Box,
  Stack,
  tokens,
} from '@cast/design-system';
import { OrganizationAvatar } from '@cast/shared-organization-ui';
import { makeValidateSync } from '@cast/utils';

import { useAbility } from '@cast/shared-ability-data-access';
import { RffForm, RffInput } from '@cast/shared-rff-ui';
import { useOrganizations } from '@cast/shared-organization-context-data-access';

import { LogoSmallSvg } from '../_assets/LogoSmallSvg';
import {
  CreateOrganizationAsEnum,
  OrganizationFormValues,
} from '../types/create-organization';

type Actions = {
  onSubmit: (value: OrganizationFormValues) => void;
  onCancel: () => void;
};

type Props = PropsWithChildren<
  {
    organizationId?: string;
    submitting: boolean;
    initialValues?: OrganizationFormValues;
    errors?: string;
    enterpriseOrgId?: string;
  } & Actions
>;

const createOrganizationSchema = yup.object({
  name: yup
    .string()
    .required('Please set an organization name.')
    .matches(
      /^[a-zA-Z\s-._0-9]+$/,
      'Invalid company name - only alphabets, numbers and ".-_" are allowed'
    ),
  createOrganizationAs: yup.string().optional(),
});

export const OrganizationForm = ({
  organizationId,
  initialValues,
  submitting,
  errors,
  children,
  onSubmit,
  onCancel,
  enterpriseOrgId,
}: Props) => {
  const canEditEnterpriseOrg = useAbility().can(
    'update',
    'EnterpriseManagement'
  );
  const validate = makeValidateSync(createOrganizationSchema);
  const inEditMode = Boolean(initialValues);
  const { organizations } = useOrganizations();
  const accessToEnterprise = organizations?.find(
    (o) => o.type === 'ORGANIZATION_TYPE_ENTERPRISE'
  );
  const enterprise = organizations?.find((org) => org.id === enterpriseOrgId);

  return (
    <RffForm
      initialValues={{
        ...initialValues,
        createOrganizationAs:
          enterprise && canEditEnterpriseOrg
            ? CreateOrganizationAsEnum.CHILD
            : CreateOrganizationAsEnum.STANDALONE,
        enterpriseId: enterprise?.id ?? accessToEnterprise?.id,
      }}
      initialValuesEqual={(a, b) => a === b}
      onSubmit={onSubmit}
      validate={validate}
      submitting={submitting}
      keepDirtyOnReinitialize
    >
      {({ values, form }) => {
        return (
          <Box>
            <Stack
              gap={40}
              py={20}
              px={24}
              direction="row"
              flexWrap="nowrap"
              width="100%"
              alignItems="flex-start"
            >
              <Stack direction="column" flex={1}>
                <Stack>{children}</Stack>
              </Stack>
              <Stack flexBasis={100} alignSelf="flex-start">
                {values.createOrganizationAs ===
                CreateOrganizationAsEnum.STANDALONE ? (
                  <Box position="relative" mr={20}>
                    <OrganizationAvatar
                      organizationId={organizationId}
                      placeholderName={values?.name}
                      size="2xl"
                    />
                    {!values?.name && (
                      <Stack
                        justifyContent="center"
                        alignItems="center"
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                        }}
                      >
                        <LogoSmallSvg color="#fff" height={52} width={52} />
                      </Stack>
                    )}
                  </Box>
                ) : (
                  <Stack mr={24} position="relative">
                    <Stack mr={-4}>
                      <OrganizationAvatar
                        organizationId={values.enterpriseId}
                        size="2xl"
                      />
                    </Stack>
                    <Box position="absolute" bottom={-20} right={-22}>
                      <OrganizationAvatar
                        organizationId={organizationId}
                        placeholderName={values?.name}
                        size="lg"
                      />
                      {!values?.name && (
                        <Stack
                          justifyContent="center"
                          alignItems="center"
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                          }}
                        >
                          <LogoSmallSvg color="#fff" height="50%" />
                        </Stack>
                      )}
                    </Box>
                  </Stack>
                )}
              </Stack>
            </Stack>
            {accessToEnterprise && !inEditMode ? (
              <>
                <Typography
                  px={24}
                  mb={4}
                  variant="P12M"
                  color={tokens.theme.fg.structural.primary}
                >
                  What you would like to create?
                </Typography>
                <RadioGroup
                  value={form.getState().values?.createOrganizationAs}
                  row
                  onChange={(_, value) =>
                    !submitting &&
                    (form as any).change('createOrganizationAs', value)
                  }
                  sx={{ gap: 8, px: 24 }}
                >
                  <FormControlLabel
                    sx={{
                      m: 0,
                      flex: 1,
                      padding: 12,
                      borderRadius: '4px !important',
                      borderWidth: 1,
                      borderStyle: 'solid',
                      borderColor:
                        values?.createOrganizationAs ===
                        CreateOrganizationAsEnum.STANDALONE
                          ? 'blue.500'
                          : 'grey.200',
                      alignItems: 'center',
                    }}
                    control={
                      <Radio
                        size="small"
                        value={CreateOrganizationAsEnum.STANDALONE}
                        color="primary"
                        disabled={submitting}
                        data-testid={`${CreateOrganizationAsEnum.STANDALONE}-organization-radio`}
                      />
                    }
                    label={
                      <Stack justifyContent="center">
                        <Typography
                          ml={8}
                          variant="P12M"
                          color={tokens.theme.fg.structural.primary}
                        >
                          Standalone organization
                        </Typography>
                      </Stack>
                    }
                  />
                  <FormControlLabel
                    sx={{
                      m: 0,
                      flex: 1,
                      padding: 12,
                      borderRadius: '4px !important',
                      borderWidth: 1,
                      borderStyle: 'solid',
                      borderColor:
                        values?.createOrganizationAs ===
                        CreateOrganizationAsEnum.CHILD
                          ? 'blue.500'
                          : 'grey.200',
                      alignItems: 'center',
                    }}
                    control={
                      <Radio
                        size="small"
                        value={CreateOrganizationAsEnum.CHILD}
                        color="primary"
                        disabled={submitting || !canEditEnterpriseOrg}
                        data-testid={`${CreateOrganizationAsEnum.CHILD}-organization-radio`}
                      />
                    }
                    label={
                      <ConditionalWrapper
                        condition={!canEditEnterpriseOrg}
                        wrapper={(child) => (
                          <Tooltip
                            title="You do not have permissions to create a child organization"
                            placement="top-end"
                          >
                            {child}
                          </Tooltip>
                        )}
                      >
                        <Stack
                          justifyContent="center"
                          ml={8}
                          direction="column"
                        >
                          <Typography
                            variant="P12M"
                            color={tokens.theme.fg.structural.primary}
                          >
                            Child organization to:
                          </Typography>
                          <Typography
                            variant="P12R"
                            color={tokens.theme.fg.structural.secondary}
                          >
                            {enterprise?.name ?? accessToEnterprise?.name}
                          </Typography>
                        </Stack>
                      </ConditionalWrapper>
                    }
                  />
                </RadioGroup>
              </>
            ) : null}
            <RffInput
              {...(errors ? { error: errors } : {})}
              testId="organization-name-input"
              name="name"
              placeholder="Name"
              required
              fullWidth
              disabled={submitting}
              inputProps={{ 'aria-label': 'Organization name input' }}
              sx={{ my: 16, px: 24 }}
            />

            <Divider color={tokens.theme.border.structural.neutralSubtle} />

            <DialogActions>
              <Button onClick={onCancel} variant="secondary">
                Cancel
              </Button>

              <Button
                type="submit"
                disabled={submitting}
                data-testid="submit-button"
              >
                {inEditMode ? 'Save changes' : 'Create'}
              </Button>
            </DialogActions>
          </Box>
        );
      }}
    </RffForm>
  );
};
